// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { addDays, format, isBefore } from "date-fns";
import React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { formatMonthYear } from "@/lib/date-formatting";

interface ProcessedData {
  data: Array<{
    rawDate: string;
    timestamp: number;
    touchpoints: number;
    cumulative: number;
  }>;
}

interface SalespersonEngagementChartProps {
  data: Array<{
    date: string;
    engagement_count: number;
    unique_people: number;
    cumulative_engagements?: number;
    cumulative_people?: number;
  }>;
  title?: string;
  isLoading?: boolean;
}

export function SalespersonEngagementChart({
  data,
  title = "Salesperson engagement over time",
  isLoading = false,
}: SalespersonEngagementChartProps) {
  const [showCumulative, setShowCumulative] = React.useState(false);

  // Process the raw data similar to campaign chart
  const processedData = React.useMemo<ProcessedData>(() => {
    if (!data?.length) {
      return {
        data: [],
      };
    }

    // Map the API data to our chart format
    const chartData = data.map((item) => ({
      rawDate: item.date,
      timestamp: new Date(item.date).getTime(),
      touchpoints: item.engagement_count || 0,
    }));

    // Sort by date
    const sortedData = chartData.sort((a, b) => a.timestamp - b.timestamp);

    // Fill in missing dates with zero values
    const dateMap = new Map(sortedData.map((item) => [item.timestamp, item]));

    if (sortedData.length > 0) {
      const startDate = addDays(new Date(sortedData[0].rawDate), -5);
      const endDate = addDays(
        new Date(sortedData[sortedData.length - 1].rawDate),
        7,
      );

      // Add entries for missing dates with zero touchpoints
      for (
        let currentDate = new Date(startDate);
        isBefore(currentDate, endDate);
        currentDate = addDays(currentDate, 1)
      ) {
        const timestamp = currentDate.getTime();
        if (!dateMap.has(timestamp)) {
          dateMap.set(timestamp, {
            rawDate: format(currentDate, "yyyy-MM-dd"),
            timestamp,
            touchpoints: 0,
          });
        }
      }
    }

    // Convert map back to array and sort chronologically
    const completeData = Array.from(dateMap.values()).sort(
      (a, b) => a.timestamp - b.timestamp,
    );

    // Calculate cumulative values
    const finalData = completeData.reduce<
      Array<{
        rawDate: string;
        timestamp: number;
        touchpoints: number;
        cumulative: number;
      }>
    >((acc, curr, index) => {
      const prevCumulative = index > 0 ? acc[index - 1].cumulative : 0;
      const currentTouchpoints = curr.touchpoints || 0;
      acc.push({
        ...curr,
        touchpoints: currentTouchpoints,
        cumulative: prevCumulative + currentTouchpoints,
      });
      return acc;
    }, []);

    return {
      data: finalData,
    };
  }, [data]);

  const { data: processedDataArray } = processedData;

  // Calculate Y-axis ticks based on current view mode (daily or cumulative)
  const calculateYAxisTicks = React.useMemo(() => {
    if (!processedDataArray?.length) return [];

    // Calculate max value based on view mode - recalculate here to ensure sync with toggle
    const maxValue = showCumulative
      ? Math.max(...processedDataArray.map((d) => d.cumulative))
      : Math.max(...processedDataArray.map((d) => d.touchpoints));

    // Handle edge case where maxValue is 0
    if (maxValue === 0) return [0, 1, 2, 3, 4, 5];

    // Round up to next multiple of 5 for cleaner divisions
    const roundedMax = Math.ceil(maxValue / 5) * 5;
    return Array.from({ length: 6 }, (_, i) =>
      Math.min(maxValue, Math.round((i * roundedMax) / 5)),
    );
  }, [processedDataArray, showCumulative]);

  // Calculate max Y value for domain (with padding)
  const maxYValue = React.useMemo(() => {
    if (!processedDataArray?.length || calculateYAxisTicks.length === 0)
      return 10;

    // Use the max tick value and add some padding
    const maxTick = calculateYAxisTicks[calculateYAxisTicks.length - 1];
    return Math.max(10, Math.ceil(maxTick * 1.1));
  }, [processedDataArray, calculateYAxisTicks]);

  return (
    <div>
      <div className="pb-4 flex items-center justify-between">
        <h3 className="text-base font-medium">{title}</h3>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">
              Show cumulative
            </span>
            <InfoTooltip
              trigger={
                <button
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InfoCircledIcon className="h-3 w-3" />
                </button>
              }
              side="top"
              className="w-[250px]"
            >
              <div className="text-sm">
                <h4 className="font-medium mb-1">Cumulative View</h4>
                <p className="text-muted-foreground">
                  Shows the total cumulative engagements over time, helping you
                  understand the overall growth trend of salesperson engagement.
                </p>
              </div>
            </InfoTooltip>
          </div>
          <Switch
            checked={showCumulative}
            onCheckedChange={setShowCumulative}
            aria-label="Toggle cumulative view"
            className="scale-75 data-[state=checked]:bg-primary/80"
          />
        </div>
      </div>
      {isLoading ? (
        <div className="flex h-[300px] w-full items-center justify-center">
          <Skeleton className="h-[300px] w-full" />
        </div>
      ) : !processedDataArray?.length ? (
        <div className="flex h-[300px] w-full items-center justify-center">
          <p className="text-sm text-muted-foreground">No data available</p>
        </div>
      ) : (
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={processedDataArray}
              margin={{ top: 30, right: 30, left: 0, bottom: 10 }}
            >
              <CartesianGrid
                vertical={false}
                stroke="hsl(var(--muted))"
                opacity={0}
              />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatMonthYear}
                tick={{ fontSize: 12 }}
                minTickGap={30}
                type="category"
                domain={["auto", "auto"]}
                stroke="hsl(var(--muted-foreground))"
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12 }}
                stroke="hsl(var(--muted-foreground))"
                domain={[0, maxYValue]}
                ticks={calculateYAxisTicks}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid var(--border)",
                  borderRadius: "0.5rem",
                  boxShadow: "0 2px 4px hsl(var(--foreground) / 0.1)",
                  fontSize: "0.75rem",
                  padding: "0.5rem",
                  color: "hsl(240 10% 3.9%)",
                }}
                formatter={(value: number, name: string) => {
                  if (name === "touchpoints")
                    return [value, "Daily Engagements"];
                  if (name === "cumulative")
                    return [value, "Total Engagements"];
                  return [value, name];
                }}
                labelFormatter={(rawDate) => {
                  // Convert timestamp to date string
                  return new Date(rawDate).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  });
                }}
              />
              <Bar
                dataKey="touchpoints"
                yAxisId="left"
                opacity={0.8}
                fill={
                  !showCumulative
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))"
                }
              />
              {showCumulative && (
                <Line
                  type="monotone"
                  dataKey="cumulative"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  yAxisId="left"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
